import ExcelJS from "exceljs";
import type { MonthBucket, OrderRow, StateBucket } from "./types.js";

const NAVY = "FF1F3864";
const GOLD = "FFFFE699";
const WHITE = "FFFFFFFF";
const PINK = "FFFFC7CE";
const RED_TEXT = "FF9C0006";
const ZEBRA = "FFF2F2F2";
const HEADER_TEXT = "FFFFFFFF";

const CURRENCY_FMT = '"$"#,##0.00;[Red]("$"#,##0.00);"-"';

type ColType = "date" | "text" | "postal" | "currency";
interface ColumnDef {
  key: keyof OrderRow | string;
  header: string;
  width: number;
  type: ColType;
  wrap?: boolean;
  source: "Day" | "Order name" | "Order Summary" | "Billing region"
    | "Shipping region" | "Shipping city" | "Shipping country"
    | "Shipping postal code" | "Discounts" | "Returns" | "Gross sales"
    | "Net sales" | "Taxes" | "Total sales";
}

const DETAIL_COLUMNS: ColumnDef[] = [
  { key: "Day",             header: "Day",                  width: 12, type: "date",     source: "Day" },
  { key: "Order name",      header: "Order name",           width: 14, type: "text",     source: "Order name" },
  { key: "Order Summary",   header: "Order Summary",        width: 50, type: "text",     source: "Order Summary", wrap: true },
  { key: "Billing region",  header: "Billing region",       width: 18, type: "text",     source: "Billing region" },
  { key: "Shipping region", header: "Shipping region",      width: 18, type: "text",     source: "Shipping region" },
  { key: "Shipping city",   header: "Shipping city",        width: 18, type: "text",     source: "Shipping city" },
  { key: "Shipping country",header: "Shipping country",     width: 16, type: "text",     source: "Shipping country" },
  { key: "Shipping postal code", header: "Shipping postal code", width: 14, type: "postal", source: "Shipping postal code" },
  { key: "Discounts",       header: "Discounts",            width: 12, type: "currency", source: "Discounts" },
  { key: "Returns",         header: "Returns",              width: 12, type: "currency", source: "Returns" },
  { key: "Net Sales",       header: "Net Sales",            width: 13, type: "currency", source: "Net sales" },
  { key: "Gross Sales",     header: "Gross Sales",          width: 13, type: "currency", source: "Gross sales" },
  { key: "Taxes",           header: "Taxes",                width: 12, type: "currency", source: "Taxes" },
  { key: "Total Sales",     header: "Total Sales",          width: 13, type: "currency", source: "Total sales" },
];

// Map header name -> 1-based column index for formula building.
function colIndexByHeader(header: string): number {
  const idx = DETAIL_COLUMNS.findIndex((c) => c.header === header);
  if (idx < 0) throw new Error(`Unknown column: ${header}`);
  return idx + 1;
}

function colLetter(idx1Based: number): string {
  let n = idx1Based;
  let out = "";
  while (n > 0) {
    const rem = (n - 1) % 26;
    out = String.fromCharCode(65 + rem) + out;
    n = Math.floor((n - 1) / 26);
  }
  return out;
}

function safeSheetName(raw: string): string {
  const cleaned = raw.replace(/[:\\/?*\[\]]/g, " ").trim();
  return cleaned.length > 31 ? cleaned.slice(0, 31) : cleaned;
}

function monthRange(year: number, month: number): string {
  const first = new Date(Date.UTC(year, month, 1));
  const last = new Date(Date.UTC(year, month + 1, 0));
  const fmt = (d: Date) =>
    `${d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric", timeZone: "UTC" })}`;
  return `${fmt(first)} – ${fmt(last)}`;
}

function fullMonthLabel(year: number, month: number): string {
  return new Date(Date.UTC(year, month, 1)).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

const HEADER_ROW = 12;          // detail header row
const DATA_START_ROW = 13;      // first data row
// We size formulas to a large fixed end row so deletions inside the range still
// behave correctly. SUM ignores blanks, so trailing empty rows are harmless.
const DATA_END_ROW = 100000;

function rangeFor(header: string): string {
  const col = colLetter(colIndexByHeader(header));
  return `${col}${DATA_START_ROW}:${col}${DATA_END_ROW}`;
}

interface ReportBucket {
  /** Sheet name suffix appended to the state, e.g. "Jan 2026" or "Q1 2026 REPORT". */
  sheetSuffix: string;
  /** Used in row-1/row-2/row-3 titles; the full period label, e.g. "January 2026" or "Q1 2026 (January – March)". */
  fullLabel: string;
  /** Subtitle "Sales Tax Summary (<range>)". */
  range: string;
  orders: OrderRow[];
}

function buildSheet(
  wb: ExcelJS.Workbook,
  state: string,
  bucket: ReportBucket,
): void {
  const sheetName = safeSheetName(`${state} ${bucket.sheetSuffix}`);
  const colCount = DETAIL_COLUMNS.length;
  const ws = wb.addWorksheet(sheetName, {
    views: [{ state: "frozen", ySplit: HEADER_ROW }],
    pageSetup: {
      orientation: "landscape",
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
      printTitlesRow: `${HEADER_ROW}:${HEADER_ROW}`,
    },
  });

  // Row 1: big title
  ws.mergeCells(1, 1, 1, colCount);
  const titleCell = ws.getCell(1, 1);
  titleCell.value = `${state} — ${bucket.fullLabel}`;
  titleCell.font = { name: "Calibri", size: 22, bold: true, color: { argb: NAVY } };
  titleCell.alignment = { horizontal: "left", vertical: "middle" };
  ws.getRow(1).height = 32;

  // Row 2: bold period label
  ws.mergeCells(2, 1, 2, colCount);
  const monthCell = ws.getCell(2, 1);
  monthCell.value = bucket.fullLabel;
  monthCell.font = { name: "Calibri", size: 14, bold: true, color: { argb: NAVY } };
  monthCell.alignment = { horizontal: "left", vertical: "middle" };

  // Row 3: subtitle
  ws.mergeCells(3, 1, 3, colCount);
  const subCell = ws.getCell(3, 1);
  subCell.value = `Sales Tax Summary (${bucket.range})`;
  subCell.font = { name: "Calibri", size: 11, italic: true, color: { argb: "FF595959" } };

  // Rows 5–7: five headline metric boxes laid out across colCount columns.
  // Each metric occupies a label cell (1 col wide, merged across 2 rows) + a
  // value cell (also merged across 2 rows). Five metrics × 2 cells = 10 cols
  // wide; we lay them out side-by-side starting at column 1.
  const metrics: { label: string; formula: string }[] = [
    { label: "GROSS SALES",                 formula: `SUM(${rangeFor("Gross Sales")})` },
    { label: "NET SALES (REPORTABLE)",      formula: `SUM(${rangeFor("Net Sales")})` },
    { label: "TOTAL SALES TAX COLLECTED",   formula: `SUM(${rangeFor("Taxes")})` },
    { label: "TOTAL DISCOUNTS",             formula: `SUM(${rangeFor("Discounts")})` },
    { label: "TOTAL RETURNS",               formula: `SUM(${rangeFor("Returns")})` },
  ];

  const blockWidth = Math.max(1, Math.floor(colCount / metrics.length));
  const valueColsPerBlock = Math.max(1, blockWidth - 1);
  metrics.forEach((m, i) => {
    const startCol = 1 + i * blockWidth;
    const labelLastCol = startCol; // single-cell label
    const valueStartCol = startCol + 1;
    const valueLastCol = startCol + valueColsPerBlock;
    setMetricBox(ws, 5, startCol, labelLastCol, valueStartCol, valueLastCol, m.label, m.formula);
  });

  // Row 8: spacing
  // Row 9: count note
  ws.mergeCells(9, 1, 9, colCount);
  const noteCell = ws.getCell(9, 1);
  noteCell.value = {
    formula:
      `"Orders: " & COUNTA(${colLetter(colIndexByHeader("Order name"))}${DATA_START_ROW}:${colLetter(colIndexByHeader("Order name"))}${DATA_END_ROW}) & " · Rows highlighted in pink = replacement orders (Gross Sales = 0)"`,
  };
  noteCell.font = { name: "Calibri", size: 10, italic: true, color: { argb: "FF595959" } };
  noteCell.alignment = { horizontal: "left", vertical: "middle" };

  // Row 11: section heading
  ws.mergeCells(11, 1, 11, colCount);
  const sectionCell = ws.getCell(11, 1);
  sectionCell.value = "Order Details";
  sectionCell.font = { name: "Calibri", size: 13, bold: true, color: { argb: NAVY } };
  sectionCell.alignment = { horizontal: "left", vertical: "middle" };

  // Row 12: header row (manually rendered — no Excel Table, so formulas above
  // use plain A1 ranges that Numbers/Google Sheets/Excel all understand).
  DETAIL_COLUMNS.forEach((c, i) => {
    const cell = ws.getCell(HEADER_ROW, i + 1);
    cell.value = c.header;
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: NAVY } };
    cell.font = { name: "Calibri", size: 11, bold: true, color: { argb: HEADER_TEXT } };
    cell.alignment = { horizontal: "left", vertical: "middle", wrapText: true };
    cell.border = thinBorder();
  });
  ws.getRow(HEADER_ROW).height = 24;

  // Apply column widths.
  DETAIL_COLUMNS.forEach((c, i) => {
    ws.getColumn(i + 1).width = c.width;
  });

  // Data rows
  bucket.orders.forEach((order, rowOffset) => {
    const rIdx = DATA_START_ROW + rowOffset;
    const row = ws.getRow(rIdx);
    const isReplacement = order.isReplacement;

    DETAIL_COLUMNS.forEach((c, i) => {
      const cell = row.getCell(i + 1);
      const v = (order as Record<string, unknown>)[c.source];

      switch (c.type) {
        case "date": {
          // Day is stored as a timezone-free CalendarDate. Writing it as a
          // UTC-midnight JS Date causes ExcelJS to emit a clean integer Excel
          // serial number (e.g. 46023 for 2026-01-01), which displays the
          // same calendar day in every spreadsheet app on every machine.
          const cd = v as { y: number; m: number; d: number } | null | undefined;
          if (cd && typeof cd === "object" && "y" in cd) {
            cell.value = new Date(Date.UTC(cd.y, cd.m - 1, cd.d));
          } else if (v != null) {
            cell.value = String(v);
          }
          cell.numFmt = "mm/dd/yyyy";
          cell.alignment = { horizontal: "left", vertical: "top" };
          break;
        }
        case "currency": {
          cell.value = typeof v === "number" ? v : Number(v ?? 0);
          cell.numFmt = CURRENCY_FMT;
          cell.alignment = { horizontal: "right", vertical: "top" };
          break;
        }
        case "postal": {
          cell.value = v == null ? "" : String(v);
          cell.numFmt = "@";
          cell.alignment = { horizontal: "left", vertical: "top" };
          break;
        }
        case "text": {
          cell.value = v == null ? "" : String(v);
          cell.alignment = { horizontal: "left", vertical: "top", wrapText: !!c.wrap };
          break;
        }
      }
      cell.border = thinBorder();

      if (isReplacement) {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: PINK } };
        cell.font = { name: "Calibri", size: 11, bold: true, color: { argb: RED_TEXT } };
      } else if (rowOffset % 2 === 1) {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: ZEBRA } };
      }
    });

    // Auto-size row height based on newlines in the Order Summary cell.
    const summary = String(order["Order Summary"] ?? "");
    const lineCount = (summary.match(/\n/g)?.length ?? 0) + 1;
    if (lineCount > 1) {
      row.height = Math.min(15 * lineCount + 4, 240);
    }
  });

  // Enable an AutoFilter on the header row so users can still sort/filter,
  // even though we're no longer using an Excel Table.
  const lastDataRow = DATA_START_ROW + bucket.orders.length - 1;
  if (lastDataRow >= DATA_START_ROW) {
    ws.autoFilter = {
      from: { row: HEADER_ROW, column: 1 },
      to: { row: lastDataRow, column: colCount },
    };
  }
}

function setMetricBox(
  ws: ExcelJS.Worksheet,
  startRow: number,
  labelStartCol: number,
  labelEndCol: number,
  valueStartCol: number,
  valueEndCol: number,
  label: string,
  formula: string,
): void {
  // Label spans two rows (label + secondary line) to give the box height.
  ws.mergeCells(startRow, labelStartCol, startRow + 1, labelEndCol);
  const labelCell = ws.getCell(startRow, labelStartCol);
  labelCell.value = label;
  labelCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: NAVY } };
  labelCell.font = { name: "Calibri", size: 10, bold: true, color: { argb: WHITE } };
  labelCell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
  labelCell.border = thinBorder();

  ws.mergeCells(startRow, valueStartCol, startRow + 1, valueEndCol);
  const valCell = ws.getCell(startRow, valueStartCol);
  valCell.value = { formula };
  valCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: GOLD } };
  valCell.font = { name: "Calibri", size: 14, bold: true, color: { argb: NAVY } };
  valCell.alignment = { horizontal: "center", vertical: "middle" };
  valCell.numFmt = CURRENCY_FMT;
  valCell.border = thinBorder();

  ws.getRow(startRow).height = 22;
  ws.getRow(startRow + 1).height = 22;
}

function thinBorder(): ExcelJS.Borders {
  const side = { style: "thin" as const, color: { argb: "FFD9D9D9" } };
  return { top: side, left: side, bottom: side, right: side } as ExcelJS.Borders;
}

/**
 * A quarter scheme defines how a state groups its three filing months and
 * how the resulting tab is labeled. `quarterOf` returns a stable
 * (periodYear, quarterIndex) for a given (year, month) — periodYear is the
 * calendar year of the LAST month in the quarter (so e.g. Dec 2025 + Jan/Feb
 * 2026 all map to periodYear=2026, q=4 under New York's scheme). This lets
 * months from two different calendar years roll up into the same filing.
 */
interface QuarterScheme {
  /** Returns null if this state does not produce a quarterly report at all. */
  quarterOf(year: number, month: number): { periodYear: number; quarterIndex: number } | null;
  /** All three (year, month) pairs that belong to the given quarter, in order. */
  monthsOf(periodYear: number, quarterIndex: number): { year: number; month: number }[];
  /** Tab suffix, e.g. "Q1 2026 REPORT". */
  suffix(periodYear: number, quarterIndex: number): string;
  /** Title in row 1/2, e.g. "Q1 2026 (January – March)". */
  fullLabel(periodYear: number, quarterIndex: number): string;
}

/**
 * Calendar quarters: Jan–Mar (Q1), Apr–Jun (Q2), Jul–Sep (Q3), Oct–Dec (Q4).
 * Used by all quarterly states except New York.
 */
const CALENDAR_QUARTER_SCHEME: QuarterScheme = {
  quarterOf(year, month) {
    return { periodYear: year, quarterIndex: Math.floor(month / 3) + 1 };
  },
  monthsOf(periodYear, quarterIndex) {
    const start = (quarterIndex - 1) * 3;
    return [0, 1, 2].map((o) => ({ year: periodYear, month: start + o }));
  },
  suffix(periodYear, quarterIndex) {
    return `Q${quarterIndex} ${periodYear} REPORT`;
  },
  fullLabel(periodYear, quarterIndex) {
    const months = this.monthsOf(periodYear, quarterIndex);
    const first = new Date(Date.UTC(months[0].year, months[0].month, 1));
    const last = new Date(Date.UTC(months[2].year, months[2].month, 1));
    const startName = first.toLocaleDateString("en-US", { month: "long", timeZone: "UTC" });
    const endName = last.toLocaleDateString("en-US", { month: "long", timeZone: "UTC" });
    return `Q${quarterIndex} ${periodYear} (${startName} – ${endName})`;
  },
};

/**
 * New York sales-tax quarters. NY's filing year ends Feb 28/29, so its
 * quarters are offset by one month from calendar quarters AND the final
 * quarter spans two calendar years:
 *   Q1: Mar – May
 *   Q2: Jun – Aug
 *   Q3: Sep – Nov
 *   Q4: Dec (year N) – Jan, Feb (year N+1)
 * The "period year" for Q4 is the year of its February (the last month),
 * so a December 2025 + January/February 2026 filing is "Q4 2026".
 */
const NEW_YORK_QUARTER_SCHEME: QuarterScheme = {
  quarterOf(year, month) {
    // month is 0-indexed (Jan=0). Map to NY quarters:
    // Mar(2)–May(4) -> Q1, Jun(5)–Aug(7) -> Q2, Sep(8)–Nov(10) -> Q3,
    // Dec(11) -> Q4 of year+1, Jan(0)–Feb(1) -> Q4 of year.
    if (month >= 2 && month <= 4) return { periodYear: year, quarterIndex: 1 };
    if (month >= 5 && month <= 7) return { periodYear: year, quarterIndex: 2 };
    if (month >= 8 && month <= 10) return { periodYear: year, quarterIndex: 3 };
    if (month === 11) return { periodYear: year + 1, quarterIndex: 4 };
    // Jan or Feb
    return { periodYear: year, quarterIndex: 4 };
  },
  monthsOf(periodYear, quarterIndex) {
    switch (quarterIndex) {
      case 1: return [{ year: periodYear, month: 2 }, { year: periodYear, month: 3 }, { year: periodYear, month: 4 }];
      case 2: return [{ year: periodYear, month: 5 }, { year: periodYear, month: 6 }, { year: periodYear, month: 7 }];
      case 3: return [{ year: periodYear, month: 8 }, { year: periodYear, month: 9 }, { year: periodYear, month: 10 }];
      case 4: return [{ year: periodYear - 1, month: 11 }, { year: periodYear, month: 0 }, { year: periodYear, month: 1 }];
      default: throw new Error(`Invalid NY quarter index: ${quarterIndex}`);
    }
  },
  suffix(periodYear, quarterIndex) {
    if (quarterIndex === 4) {
      // Dec of (periodYear-1) through Feb of periodYear; show both years for clarity.
      return `Q4 ${periodYear - 1}-${periodYear} REPORT`;
    }
    return `Q${quarterIndex} ${periodYear} REPORT`;
  },
  fullLabel(periodYear, quarterIndex) {
    const months = this.monthsOf(periodYear, quarterIndex);
    const first = new Date(Date.UTC(months[0].year, months[0].month, 1));
    const last = new Date(Date.UTC(months[2].year, months[2].month, 1));
    const startName = first.toLocaleDateString("en-US", { month: "long", timeZone: "UTC" });
    const endName = last.toLocaleDateString("en-US", { month: "long", timeZone: "UTC" });
    if (quarterIndex === 4) {
      // Spans two calendar years — include both in the title.
      return `Q4 ${periodYear - 1}-${periodYear} (${startName} ${months[0].year} – ${endName} ${months[2].year})`;
    }
    return `Q${quarterIndex} ${periodYear} (${startName} – ${endName})`;
  },
};

// States that get an extra `Q<N> REPORT` tab whenever the upload covers an
// entire filing quarter for that state. New York uses its own non-calendar
// scheme; the others use calendar quarters.
const QUARTER_SCHEMES: Record<string, QuarterScheme> = {
  California: CALENDAR_QUARTER_SCHEME,
  Maryland: CALENDAR_QUARTER_SCHEME,
  Michigan: CALENDAR_QUARTER_SCHEME,
  Minnesota: CALENDAR_QUARTER_SCHEME,
  "New Jersey": CALENDAR_QUARTER_SCHEME,
  "New York": NEW_YORK_QUARTER_SCHEME,
  Pennsylvania: CALENDAR_QUARTER_SCHEME,
};

function monthToReportBucket(m: MonthBucket): ReportBucket {
  return {
    sheetSuffix: m.monthLabel,
    fullLabel: fullMonthLabel(m.year, m.month),
    range: monthRange(m.year, m.month),
    orders: m.orders,
  };
}

function quarterReportBucket(
  scheme: QuarterScheme,
  periodYear: number,
  quarterIndex: number,
  monthBuckets: MonthBucket[],
): ReportBucket {
  // Re-sort the combined orders the same way bucketByStateAndMonth does:
  // replacements first, then by Day, then by Order name.
  const merged = monthBuckets.flatMap((m) => m.orders).slice();
  merged.sort((a, b) => {
    if (a.isReplacement !== b.isReplacement) return a.isReplacement ? -1 : 1;
    const ad = a["Day"];
    const bd = b["Day"];
    const ak = ad ? ad.y * 10000 + ad.m * 100 + ad.d : 0;
    const bk = bd ? bd.y * 10000 + bd.m * 100 + bd.d : 0;
    if (ak !== bk) return ak - bk;
    return String(a["Order name"]).localeCompare(String(b["Order name"]));
  });

  const months = scheme.monthsOf(periodYear, quarterIndex);
  const first = new Date(Date.UTC(months[0].year, months[0].month, 1));
  // Last day of the final month in the quarter.
  const last = new Date(Date.UTC(months[2].year, months[2].month + 1, 0));
  const fmt = (d: Date) =>
    d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric", timeZone: "UTC" });

  return {
    sheetSuffix: scheme.suffix(periodYear, quarterIndex),
    fullLabel: scheme.fullLabel(periodYear, quarterIndex),
    range: `${fmt(first)} – ${fmt(last)}`,
    orders: merged,
  };
}

/**
 * Returns the sequence of report tabs to render for one state. If the state
 * has a quarter scheme and the upload contains ALL three months of a given
 * quarter (per that state's scheme), a quarter tab is inserted right after
 * the last month of that quarter has been rendered.
 */
function reportBucketsFor(state: string, monthBuckets: MonthBucket[]): ReportBucket[] {
  const out: ReportBucket[] = [];
  const scheme = QUARTER_SCHEMES[state];

  // Group available months by (periodYear, quarterIndex) for fast lookup.
  // Each unique (year, month) is added once.
  const byPeriodQuarter = new Map<string, MonthBucket[]>();
  if (scheme) {
    for (const m of monthBuckets) {
      const q = scheme.quarterOf(m.year, m.month);
      if (!q) continue;
      const key = `${q.periodYear}-${q.quarterIndex}`;
      const arr = byPeriodQuarter.get(key) ?? [];
      arr.push(m);
      byPeriodQuarter.set(key, arr);
    }
  }

  for (let i = 0; i < monthBuckets.length; i++) {
    const m = monthBuckets[i];
    out.push(monthToReportBucket(m));

    if (!scheme) continue;
    const q = scheme.quarterOf(m.year, m.month);
    if (!q) continue;
    const monthsInQuarter = byPeriodQuarter.get(`${q.periodYear}-${q.quarterIndex}`) ?? [];
    if (monthsInQuarter.length !== 3) continue;
    // Insert the quarter tab only after we've just rendered the LAST month of
    // the quarter (per the scheme's ordering). We compare against the third
    // entry in the scheme's monthsOf result.
    const expected = scheme.monthsOf(q.periodYear, q.quarterIndex);
    const lastOfQuarter = expected[2];
    if (m.year === lastOfQuarter.year && m.month === lastOfQuarter.month) {
      out.push(quarterReportBucket(scheme, q.periodYear, q.quarterIndex, monthsInQuarter));
    }
  }
  return out;
}

export async function buildStateWorkbook(bucket: StateBucket): Promise<Uint8Array> {
  const wb = new ExcelJS.Workbook();
  wb.creator = "Shopify Tax Report Tool";
  wb.created = new Date();
  const buckets = reportBucketsFor(bucket.state, bucket.months);
  for (const b of buckets) {
    buildSheet(wb, bucket.state, b);
  }
  const buf = await wb.xlsx.writeBuffer();
  return new Uint8Array(buf as ArrayBuffer);
}
